"use client";
import { useEffect } from "react";
import { useTenant } from "@/context/TenantContext";
import { isAuthenticated, getUser } from "@/services/auth";

/**
 * After login + tenant resolution, warm the route cache for every page the
 * user is likely to visit. Without this, the first navigation to a route
 * downloads its JS bundle on demand — visibly slow on lower-end networks.
 *
 * Renders nothing. Mount once near the top of the tree (layout.tsx).
 */
export default function RoutePrefetcher() {

  const { tenantData, loading } = useTenant();

  useEffect(() => {
    if (loading || !isAuthenticated()) return;

    const businessType = tenantData?.tenant?.businessType ?? "PARKING";
    const role = getUser()?.role ?? "";
    const isAdmin = role && role !== "OPERATOR";

    // Routes accessible from the header nav + dashboard quick-actions, by
    // business type. Listing them once here means the user pays for the
    // bundles in the background (warm cache hit) rather than on click.
    const routes: string[] = ["/dashboard", "/finance", "/settings"];
    if (isAdmin) routes.push("/audit-log");

    if (businessType === "PARKING") {
      routes.push("/check-in", "/check-out", "/parking-list");
    } else if (businessType === "RAILWAY_CLOAK_ROOM") {
      routes.push("/cloak-room", "/item-in", "/item-out", "/item-list");
    } else if (businessType === "ROAD_TAX") {
      routes.push("/road-tax", "/tax-collection-list");
    }

    // Also pre-trigger the dynamic-imported settings-tab components so they
    // resolve in the background instead of after the user clicks the tab.
    const dynamicChunks: Array<() => Promise<unknown>> = [];
    if (isAdmin) {
      if (businessType === "PARKING") {
        dynamicChunks.push(() => import("@/components/SystemConfig/TenantPricingConfigManagement"));
        dynamicChunks.push(() => import("@/components/SystemConfig/MonthlyPassManagement"));
      } else if (businessType === "RAILWAY_CLOAK_ROOM") {
        dynamicChunks.push(() => import("@/components/CloakRoom/ItemTypesManagement"));
        dynamicChunks.push(() => import("@/components/CloakRoom/ItemPricingManagement"));
      } else if (businessType === "ROAD_TAX") {
        dynamicChunks.push(() => import("@/components/RoadTax/VehicleTypeAdmin"));
        dynamicChunks.push(() => import("@/components/RoadTax/FlatRatePricingAdmin"));
      }
    }

    // Defer to idle so we don't compete with the initial paint. Falls back to
    // a short timeout where requestIdleCallback isn't available.
    const schedule = (cb: () => void) => {
      const ric: any = (window as any).requestIdleCallback;
      if (typeof ric === "function") ric(cb, { timeout: 2000 });
      else setTimeout(cb, 200);
    };

    schedule(() => {

      dynamicChunks.forEach(load => { load().catch(() => { /* noop */ }); });
    });
  }, [loading, tenantData?.tenant?.businessType]);

  return null;
}
